import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { ContentBlock, Loader, Typography } from '@/components';
import {
  CreateUpdateCustomerForm,
  UserCreateData
} from './CreateUpdateCustomerForm';
import { useStyles } from './styles';
import {
  addUserAgreements,
  createCustomer,
  getAgreements,
  getCustomerProvision,
  getUser,
  patchUser,
  provisionCustomer
} from '@/api';
import { useNavigate, useParams } from 'react-router-dom';
import { useIframeMessageHandler } from '@/utils';
import { VideoContext } from '@/GlobalProvider/VideoProvider';
import { RiFileUploadLine } from 'react-icons/ri';
import { BulkRegistrationContext } from '@/GlobalProvider/BulkRegistrationProvider';
import { useAppData } from '@/zustand/useAppData';
import { useAssetsData } from '@/zustand/useAssetsData';
import { useAlertData } from '@/zustand/useAlertData';
import ReactGA from 'react-ga4';
import { hashStringWithSHA256 } from '@/gtag/utils';

export const CreateCustomer = () => {
  const styles = useStyles();
  const { id } = useAppData((store) => store.appData);
  const setAlert = useAlertData((store) => store.setAlertData);
  const { setShowVideo } = useContext(VideoContext);
  const { setShowRegistrationModal } = useContext(BulkRegistrationContext);
  const {
    behavior_list: {
      prescan_video_display,
      fit_preference_url,
      enable_bulk_upload
    }
  } = useAssetsData((store) => store.assetsData) || {};
  const navigation = useNavigate();
  const params = useParams();

  const [loading, setLoading] = useState(false);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [newUserData, setNewUserData] = useState(null);

  function getNumericPhone(phone: string): string {
    return phone.replace(/\D/g, '');
  }

  const createNewUser = async (data: UserCreateData) => {
    try {
      if (data.accountId) {
        const newCustomerProvision = await getCustomerProvision(
          id,
          data.accountId
        );

        if (newCustomerProvision.id) {
          setAlert({
            text: 'A user with this subject ID already exists.',
            variation: 'error'
          });
          return;
        }
      }

      if (data.email) {
        const newCustomerProvision = await getCustomerProvision(id, data.email);

        if (newCustomerProvision.id) {
          setAlert({
            text: 'A user with this email address already exists.',
            variation: 'error'
          });
          return;
        }
      }

      if (data.phone) {
        const newCustomerProvision = await getCustomerProvision(id, data.phone);

        if (newCustomerProvision.id) {
          setAlert({
            text: 'A user with this phone number already exists.',
            variation: 'error'
          });
          return;
        }
      }

      const response = await createCustomer(data, id);

      ReactGA.event('create_customer', {
        email: hashStringWithSHA256(data.email)
      });

      if (data.accountId)
        await provisionCustomer(response.id, id, data.accountId);
      // create handle for phone if it exists
      if (data.phone) await provisionCustomer(response.id, id, data.phone);
      // create handle for email if it exists
      if (data.email) await provisionCustomer(response.id, id, data.email);

      const agreementsResponse = await getAgreements(id);
      await addUserAgreements(
        response.id,
        agreementsResponse,
        `${response.firstName} ${response.lastName}`
      );

      if (data.accountId) {
        setNewUserData(response);
        navigation(
          `/customers/${response.id}/${encodeURI(data.accountId)}?newUser=true`
        );
      } else {
        setNewUserData(response);
        navigation(`/customers/${response.id}?newUser=true`);
      }
      if (
        prescan_video_display === 'both_locations' ||
        prescan_video_display === 'after_sign_up_only'
      ) {
        setShowVideo(true);
      }
    } catch (e) {
      const error = JSON.stringify(e.message);

      if (error === '"Network Error"') {
        setAlert({
          text: 'Check your internet and try again.',
          variation: 'error'
        });
      } else {
        setAlert({
          text: 'User with this data was not found!',
          variation: 'error'
        });
      }
    }
  };

  const updateUser = async (value: UserCreateData) => {
    try {
      if (value.email && !currentCustomer.email) {
        const newCustomerProvision = await getCustomerProvision(
          id,
          value.email
        );

        if (newCustomerProvision.id) {
          setAlert({
            text: 'A user with this email address already exists.',
            variation: 'error'
          });
          return;
        }
      }

      if (value.phone && currentCustomer?.phone !== value.phone) {
        const phoneForProvision = getNumericPhone(value.phone);
        const newCustomerProvision = await getCustomerProvision(
          id,
          phoneForProvision
        );

        if (newCustomerProvision.id) {
          setAlert({
            text: 'A user with this phone number already exists.',
            variation: 'error'
          });
          return;
        }
      }

      // create handle for phone if it exists
      if (value.phone && !currentCustomer.phone)
        await provisionCustomer(currentCustomer.id, id, value.phone);
      // create handle for email if it exists
      if (value.email && !currentCustomer.email)
        await provisionCustomer(currentCustomer.id, id, value.email);

      if (currentCustomer) {
        await patchUser({ ...value, id: currentCustomer.id });

        ReactGA.event('update_customer', {
          email: hashStringWithSHA256(currentCustomer.email)
        });

        navigation(`/customers/${currentCustomer.id}/${params.accountId}`);
      }
    } catch (e) {
      const error = JSON.stringify(e.message);

      if (error === '"Network Error"') {
        setAlert({
          text: 'Check your internet and try again.',
          variation: 'error'
        });
      } else {
        setAlert({
          text: 'User with this data was not found!',
          variation: 'error'
        });
      }
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      if (params.customerId) {
        const customerData = await getUser(params.customerId);
        if (!customerData.status) {
          setCurrentCustomer({
            ...customerData,
            accountId: decodeURI(params.accountId)
          });
        }
      }
      setLoading(false);
    })();
  }, []);

  useIframeMessageHandler(currentCustomer);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      {!newUserData && (
        <ContentBlock
          hasBackButton
          backHandler={
            !!currentCustomer &&
            (() =>
              navigation(`/customers/${params.customerId}/${params.accountId}`))
          }
          leftButton={
            !currentCustomer &&
            enable_bulk_upload === 'true' && (
              <RiFileUploadLine
                onClick={() => setShowRegistrationModal(true)}
                style={{ width: '32px', height: '32px' }}
              />
            )
          }
        >
          <div className={styles.header}>
            <Typography component="h2">Welcome!</Typography>
            <Typography component="h3">
              {!!currentCustomer
                ? 'Update customer'
                : 'Sign up to get started '}
            </Typography>
          </div>
          <CreateUpdateCustomerForm
            submitHandler={currentCustomer ? updateUser : createNewUser}
            defaultValue={currentCustomer}
            isUpdateForm={!!currentCustomer}
          />
        </ContentBlock>
      )}
    </>
  );
};
